import { DialogActions, DialogContent } from '@material-ui/core';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { usePathFromHierarchy } from '../../hooks/usePathFromHierarchy';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { DialogTitle } from '../common/Dialog';
import { TreeItem, TreeItemLabel, TreeView, useFolderTreeStyles } from '../common/FolderTree';
import { Loader } from '../common/Loader';
import { Typography } from '../common/Typography';
import { useGetFolderHierarchy } from '../services/UploadManagementService';
import { Content, FolderHierarchy, ROOT_FOLDER_ID } from '../services/UploadManagementServiceTypes';

export interface MoveContentModalProps {
  content: Content;
  getFolderHierarchy: () => Promise<FolderHierarchy>;
  onClose: () => void;
  onSuccess: () => void;
  moveAction: (newParentGuid: string) => Promise<boolean>;
}

interface MoveContentModalState {
  targetFolderGuid: string;
  errorSubmit: string;
  loadingSubmit: boolean;
}

const initialState: MoveContentModalState = {
  targetFolderGuid: '',
  errorSubmit: '',
  loadingSubmit: false,
};

const getErrorText = (contentType: string, statusCode?: number) => {
  if (statusCode === 409) {
    return `The ${contentType} could not be moved. Please check if the name of the object to be moved at the target level already exists.`;
  }
  return `The ${contentType} could not be moved`;
};

const getNodeInfo = (nodeId: string, content: Content) => {
  const nodeIsContent = nodeId === content.guid;
  const nodeIsRoot = nodeId === ROOT_FOLDER_ID;
  const nodeIsParent = nodeId === content.parentGuid || (!content.parentGuid && nodeIsRoot);
  return { nodeIsContent, nodeIsParent, nodeIsRoot };
};

export const MoveContentModal: React.FC<MoveContentModalProps> = ({
  content,
  getFolderHierarchy,
  onClose,
  onSuccess,
  moveAction,
}) => {
  const [{ targetFolderGuid, loadingSubmit, errorSubmit }, setState] = useState(initialState);
  const {
    data: folderHierarchy,
    isLoading: loadingHierarchy,
    isError: errorHierarchy,
  } = useGetFolderHierarchy(getFolderHierarchy);
  const { pathToParentContent } = usePathFromHierarchy({ folderHierarchy, content });

  const loading = loadingSubmit || loadingHierarchy;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setState((state) => ({ ...state, loadingSubmit: true }));

    try {
      const response = await moveAction(targetFolderGuid);

      if (!response) {
        setState((state) => ({
          ...state,
          errorSubmit: getErrorText(content.contentType.name),
          loadingSubmit: false,
        }));
        return;
      }
      onClose();
      onSuccess();
      return;
    } catch (e: any) {
      console.error(e);
      setState((state) => ({
        ...state,
        errorSubmit: getErrorText(content.contentType.name, e.status),
        loadingSubmit: false,
      }));
    }
  };

  const handleSelect = (event: ChangeEvent<any>, nodeId: string) => {
    const { nodeIsParent, nodeIsContent } = getNodeInfo(nodeId, content);
    if (nodeIsContent || nodeIsParent) {
      return;
    }
    setState((state) => ({ ...state, targetFolderGuid: nodeId }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Move {content.contentType.name}</DialogTitle>
      <DialogContent>
        {loadingHierarchy && <Loader />}
        {folderHierarchy && (
          <TreeView
            onNodeSelect={handleSelect}
            expanded={pathToParentContent}
            selected={targetFolderGuid}
          >
            <RenderTree node={folderHierarchy} content={content} />
          </TreeView>
        )}
        {errorSubmit && <Alert severity='error' shown={true} text={errorSubmit} />}
        {errorHierarchy && (
          <Alert severity='error' shown={true} text='Could not load file hierarchy.' />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={loading}>
          <Typography variant='h5'>Cancel</Typography>
        </Button>
        <Button type='submit' disabled={loading || !targetFolderGuid.length}>
          <Typography variant='h5'>Select Folder</Typography>
        </Button>
      </DialogActions>
    </form>
  );
};

const RenderTree: React.FC<{ node: FolderHierarchy; content: Content }> = ({ node, content }) => {
  const classes = useFolderTreeStyles();
  const { nodeIsParent, nodeIsContent } = getNodeInfo(node.guid, content);

  const getLabelClass = () => {
    if (nodeIsContent) {
      return classes.treeItemCurrent;
    }
    if (nodeIsParent) {
      return classes.treeItemCurrentParent;
    }
    return classes.treeItemDefault;
  };

  return (
    <TreeItem
      classes={{
        root: classes.treeItemRoot,
        label: `${classes.treeItemBase} ${getLabelClass()}`,
      }}
      nodeId={node.guid}
      label={<TreeItemLabel name={node.name} />}
      preventNodeToggleOnLabelClick
    >
      {node.subHierarchy.map((child: FolderHierarchy, index) => (
        <RenderTree key={index} node={child} content={content} />
      ))}
    </TreeItem>
  );
};
